import * as React from "react";
import { useCheckbox } from "@react-aria/checkbox";
import { mergeProps } from "@react-aria/utils";
import { VisuallyHidden } from "@react-aria/visually-hidden";
import { useToggleState } from "@react-stately/toggle";
import mergeRefs from "merge-refs";
import { css } from "@nju/css";
import { Icon } from "@nju/icons";

export interface CheckboxProps {
  name: string;
  "aria-label"?: string;
  isDisabled?: boolean;
  isDisabledBackground?: boolean;
  isIndeterminate?: boolean;
  defaultSelected?: boolean;
  isSelected?: boolean;
  value?: string;
  checkboxPosition?: "top" | "center";
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onValueChange?: (isSelected: boolean) => void;
  state?: "error";
  isRequired?: boolean;
  width?: number;
  height?: number;
}

export const Checkbox = React.forwardRef<
  HTMLInputElement,
  React.PropsWithChildren<CheckboxProps>
>(function Checkbox(props, ref) {
  const {
    children,
    state,
    onValueChange,
    onChange,
    checkboxPosition = "center",
    width,
    height,
    isDisabledBackground,
    ...rest
  } = props;
  const innerRef = React.useRef<HTMLInputElement>(null);

  const toggleState = useToggleState({
    ...rest,
    onChange: onValueChange,
    children,
  });

  const { inputProps } = useCheckbox(
    {
      ...rest,
      onChange: onValueChange,
      children,
    },
    toggleState,
    innerRef,
  );

  const isSelected = toggleState.isSelected;

  return (
    <label className={styles.label({ checkboxPosition })}>
      <VisuallyHidden elementType="span">
        <input
          {...mergeProps(inputProps, { onChange })}
          ref={mergeRefs(ref, innerRef)}
        />
      </VisuallyHidden>

      <span
        className={styles.element({
          checked: isSelected,
          disabled: isDisabledBackground,
          state,
        })}
      >
        <Icon
          className={styles.icon({
            checked: isSelected,
          })}
          height={height}
          name="check"
          width={width}
        />
      </span>

      {children}
    </label>
  );
});

const styles = {
  label: css({
    display: "flex",
    cursor: "pointer",
    variants: {
      checkboxPosition: {
        top: {
          alignItems: "flex-start",
        },
        center: {
          alignItems: "center",
        },
      },
    },
  }),
  element: css({
    width: 20,
    height: 20,
    flex: "0 0 20px",
    marginRight: "$10",
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "$4",
    willChange: "background-color, border",
    borderWidth: "$2",
    borderStyle: "solid",
    transition: "$bgColor, $border",
    defaultVariants: { checked: "false" },
    variants: {
      checked: {
        true: {
          backgroundColor: "$daintree",
          borderColor: "$daintree",
        },
        false: {
          backgroundColor: "$white",
          borderColor: "$geyser",
        },
      },
      state: {
        error: {
          borderColor: "$tiaMaria",
        },
      },
      disabled: {
        true: {
          opacity: 0.2,
        },
      },
    },
    compoundVariants: [
      {
        checked: "true",
        state: "error",
        css: {
          backgroundColor: "$tiaMaria",
        },
      },
    ],
  }),
  icon: css({
    transition: "$transform",
    willChange: "transform",
    color: "$white",
    height: 10,
    width: 10,
    defaultVariants: { checked: "false" },
    variants: {
      checked: {
        true: {
          transform: "scale(1)",
        },
        false: {
          transform: "scale(0)",
        },
      },
    },
  }),
};
